import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  Form,
  ReferenceInput,
  SelectInput,
  TextInput,
  required,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { type AccessibleRegion } from '../auth/permissions';
import type { AdminDataProvider, Market } from '../dataProvider';
import { validUrl } from './validators';

/**
 * Charity create — the app's FIRST create surface. A create produces an
 * `action='create'` audit entry (after-snapshot, no before) automatically through
 * the #25 wrapper; there is no audit code here.
 *
 * MARKET IS EXPLICIT (D-charity-create-market): a GLOBAL admin chooses uk/us
 * deliberately; a single-region admin has market PRE-SET and LOCKED to their own
 * market and cannot create outside their scope — enforced in the submit handler,
 * not just the form (so reaching past the UI is still rejected). New charities
 * default to active.
 *
 * The route is gated `charities:write` (a viewer is denied directly, not just
 * missing the button).
 */

const regionToMarket = (region: string): Market =>
  region.toLowerCase() as Market;

interface CharityFormValues {
  name: string;
  market: string;
  charityTypeId: number;
  description: string;
  logoUrl: string;
  bannerUrl: string;
}

const CharityCreateForm = () => {
  const { accessibleRegions } = useRegion();
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  // D-charity-create-market: single-region admins are locked to their market.
  const locked = accessibleRegions.length === 1;
  const lockedMarket = locked ? regionToMarket(accessibleRegions[0]) : '';

  const defaultValues = {
    name: '',
    market: lockedMarket,
    description: '',
    logoUrl: '',
    bannerUrl: '',
  };

  const marketChoices = accessibleRegions.map((r) => ({
    id: regionToMarket(r),
    name: r,
  }));

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as CharityFormValues;
    const market = locked ? lockedMarket : values.market;

    // Region guard — cannot create a charity outside the admin's scope, even if
    // the form were bypassed.
    if (!accessibleRegions.includes(market.toUpperCase() as AccessibleRegion)) {
      notify('You cannot create a charity outside your region', {
        type: 'error',
      });
      return;
    }

    try {
      const { data } = await dataProvider.create('charities', {
        data: {
          name: values.name,
          market,
          charityTypeId: Number(values.charityTypeId),
          description: values.description,
          logoUrl: values.logoUrl ?? '',
          bannerUrl: values.bannerUrl ?? '',
          status: 'active',
          createdAt: new Date().toISOString(),
        },
      });
      notify('Charity created', { type: 'success' });
      redirect('show', 'charities', data.id);
    } catch {
      notify('Could not create the charity', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        New charity
      </Typography>
      <Form defaultValues={defaultValues} onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextInput
              source="name"
              label="Name"
              validate={required('Name is required')}
              fullWidth
            />
            {locked ? (
              // Market is fixed to the admin's region and shown read-only.
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Market
                </Typography>
                <Typography variant="body1">
                  {lockedMarket.toUpperCase()}
                </Typography>
              </Box>
            ) : (
              <SelectInput
                source="market"
                label="Market"
                choices={marketChoices}
                validate={required('Market is required')}
              />
            )}
            <ReferenceInput
              source="charityTypeId"
              reference="charityTypes"
              sort={{ field: 'label', order: 'ASC' }}
              perPage={100}
            >
              <SelectInput
                label="Charity type"
                optionText="label"
                validate={required('Charity type is required')}
              />
            </ReferenceInput>
            <TextInput
              source="description"
              label="Description"
              validate={required('Description is required')}
              multiline
              fullWidth
            />
            <TextInput
              source="logoUrl"
              label="Logo URL (optional)"
              validate={validUrl}
              fullWidth
            />
            <TextInput
              source="bannerUrl"
              label="Banner URL (optional)"
              validate={validUrl}
              fullWidth
            />
          </CardContent>
        </Card>
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Create
          </Button>
          <Button type="button" onClick={() => redirect('list', 'charities')}>
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** Gated entry point registered as the `charities` Resource's create. */
export const CharityCreate = () => (
  <RequireAccess capability="charities:write">
    <CharityCreateForm />
  </RequireAccess>
);
